import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { testimonials } from '../../../mocks/testimonials';

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const current = testimonials[active];

  return (
    <section className="py-24 bg-darkBlue-500 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #7EC8E3 0, #7EC8E3 1px, transparent 0, transparent 50%)',
            backgroundSize: '20px 20px'
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-clairBlue-300 text-xs font-bold uppercase tracking-widest mb-4 bg-clairBlue-300/10 border border-clairBlue-300/20 px-4 py-1.5 rounded-full">
            {t('testimonials.label')}
          </span>
          <h2 className="font-display text-4xl font-bold text-white mb-3">Why Choose Us</h2>
          <p className="text-gray-400 text-base">Professional services backed by results-driven strategies</p>
        </div>

        {/* Why Choose Us Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: 'ri-award-line', title: 'Professional Services', desc: 'Expert team with years of experience delivering high-quality digital solutions for businesses across Africa.' },
            { icon: 'ri-rocket-line', title: 'Fast Delivery', desc: 'Quick turnaround times without compromising quality. We respect your deadlines and deliver on time, every time.' },
            { icon: 'ri-shield-check-line', title: 'Trusted Solutions', desc: 'Proven strategies and reliable tools that actually work. Real results backed by data and measurable outcomes.' },
          ].map((item, i) => (
            <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-clairBlue-300/20 rounded-2xl">
                <i className={`${item.icon} text-clairBlue-300 text-2xl`}></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-3">{item.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <h3 className="text-white font-display text-2xl font-bold text-center mb-8">What Our Clients Say</h3>

        {/* Main Testimonial */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12 relative">
            {/* Quote Icon */}
            <i className="ri-double-quotes-l absolute top-8 left-8 text-clairBlue-300/20 text-8xl leading-none"></i>

            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <StarRating count={current.rating} />
                <span className="bg-clairBlue-300/10 border border-clairBlue-300/20 text-clairBlue-300 text-xs font-medium px-3 py-1 rounded-full">
                  {current.service}
                </span>
              </div>

              <p className="text-white text-xl lg:text-2xl font-light leading-relaxed mb-8 italic">
                "{current.text}"
              </p>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-clairBlue-300/40 flex-shrink-0">
                  <img
                    src={current.avatar}
                    alt={current.name}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(current.name)}&background=0D2340&color=ffffff`;
                    }}
                  />
                </div>
                <div>
                  <div className="text-white font-bold text-base">{current.name}</div>
                  <div className="text-gray-400 text-sm">{current.role}, {current.company}</div>
                  <div className="text-clairBlue-300 text-xs flex items-center gap-1 mt-0.5">
                    <i className="ri-map-pin-2-line"></i>
                    {current.location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-3 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === active ? 'w-8 h-3 bg-clairBlue-300' : 'w-3 h-3 bg-white/20 hover:bg-white/40'
                }`}
              ></button>
            ))}
          </div>

          {/* Arrow Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setActive(a => (a - 1 + testimonials.length) % testimonials.length)}
              className="w-11 h-11 flex items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <span className="text-gray-500 text-sm">{active + 1} / {testimonials.length}</span>
            <button
              onClick={() => setActive(a => (a + 1) % testimonials.length)}
              className="w-11 h-11 flex items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* All Client Thumbnails */}
        <div className="flex items-center justify-center gap-4 mt-12 flex-wrap">
          {testimonials.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActive(i)}
              className={`transition-all cursor-pointer ${active === i ? 'scale-110' : 'opacity-50 hover:opacity-75'}`}
            >
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-clairBlue-300/40">
                <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
